import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Upload,
  Check,
  Pencil,
  Plus,
  Settings,
  Trash2,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { OverviewToolbar } from "../components/layout/OverviewToolbar";
import {
  SegmentedToggle,
  loadStoredChoice,
  saveStoredChoice,
} from "../components/layout/ViewToggle";
import { ChevronLeft, ChevronRight, LayoutGrid, Table2, Wand2 } from "lucide-react";
import {
  DATA_SHEET_COL_TYPE,
  dataSheetsApi,
  type DataSheetColumn,
  type DataSheetDetail,
  type DataSheetRecord,
  type LookupItem,
  type LookupsResponse,
} from "../api/dataSheetsApi";
import { useCanEditSchema } from "../hooks/useCanEditSchema";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { Modal } from "../components/ui/Modal";
import { DataSheetExportModal } from "../components/datasheets/DataSheetExportModal";
import { DataSheetImportModal } from "../components/datasheets/DataSheetImportModal";

type LoadState =
  | { status: "loading" }
  | {
      status: "ready";
      detail: DataSheetDetail;
      records: DataSheetRecord[];
      lookups: LookupsResponse;
    }
  | { status: "error"; message: string };

/** Rows per page. Search always runs over the full in-memory dataset;
 *  only the rendering is paged. */
const PAGE_SIZE = 50;

/** How the records render: a classic table ("grid"), stacked cards, or
 *  automatic (cards only when the table wouldn't fit). Persisted. */
type RecordsLayout = "grid" | "cards" | "auto";
const RECORDS_LAYOUTS: readonly RecordsLayout[] = ["grid", "cards", "auto"];
const RECORDS_LAYOUT_KEY = "ds-records-layout";

/** Minimum width of an edit-mode input/select cell. Shared with the
 *  offscreen probe so edit-mode width is measured, not just display text. */
const EDIT_CELL_MIN_WIDTH = 120;

/**
 * Records page for a single data sheet. Rows are rendered read-only by
 * default with a per-row pencil icon; clicking the pencil promotes the
 * row to edit mode where cells become editable and the action cell
 * swaps to a "✓ / ✗" (save / cancel) pair. Only one row can be in edit
 * mode at a time, and while a row is dirty the Edit buttons on other
 * rows are disabled so the user can't accidentally lose changes.
 *
 * Long sheets are buffered client-side: we render the first
 * {@link PAGE_SIZE} rows per page
 * loads the next slice when it scrolls into view. Searching filters
 * the full in-memory dataset (so you never miss a match below the
 * buffer) and stretches the buffer to cover all matches.
 */
export function DataSheetRecordsPage() {
  const params = useParams<{ sheetId: string }>();
  const sheetId = Number(params.sheetId);
  const navigate = useNavigate();
  const canEditSchema = useCanEditSchema();

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [layout, setLayout] = useState<RecordsLayout>(() =>
    loadStoredChoice(RECORDS_LAYOUT_KEY, RECORDS_LAYOUTS, "auto"),
  );
  /** The row currently in edit mode (if any). Draft rows have their
   *  own `draftRow.key`; persisted rows use `persisted-<dataId>`. */
  const [activeEdit, setActiveEdit] = useState<ActiveEdit | null>(null);
  /** Error to show inside the currently editing row. */
  const [editError, setEditError] = useState<string | null>(null);
  /** true while a save/delete round-trip is in flight. */
  const [busy, setBusy] = useState(false);
  /** 1-based page of the filtered list being shown; reset to 1 whenever
   *  the filter or dataset changes. */
  const [page, setPage] = useState(1);
  /** Row-level delete errors, keyed by row.key, for inline display. */
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  /** When set, the delete-confirmation modal is shown for this row. */
  const [pendingDeleteRow, setPendingDeleteRow] = useState<EditableRow | null>(
    null,
  );

  /** Export / import dialogs (CSV / Excel). */
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const reload = async () => {
    setState({ status: "loading" });
    try {
      const [detail, recordsResp, lookups] = await Promise.all([
        dataSheetsApi.get(sheetId),
        dataSheetsApi.listRecords(sheetId),
        dataSheetsApi.lookups(),
      ]);
      setState({
        status: "ready",
        detail,
        records: recordsResp.items ?? [],
        lookups,
      });
      setActiveEdit(null);
      setEditError(null);
      setRowErrors({});
      setPage(1);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load records";
      setState({ status: "error", message });
    }
  };

  useEffect(() => {
    if (!Number.isFinite(sheetId) || sheetId <= 0) {
      setState({ status: "error", message: "Invalid data sheet id." });
      return;
    }
    void reload();
  }, [sheetId]);

  const visibleColumns = useMemo<DataSheetColumn[]>(() => {
    if (state.status !== "ready") return [];
    return state.detail.columns.filter(
      (c) => (c.name ?? "").trim().length > 0,
    );
  }, [state]);

  /** Full set of read-only rows (persisted + the currently-editing
   *  draft, if any). Draft rows without a persisted twin sit at the
   *  bottom so a newly added row is visible while the user types. */
  const allRows = useMemo<EditableRow[]>(() => {
    if (state.status !== "ready") return [];
    const persisted: EditableRow[] = state.records.map((r) => ({
      key: `persisted-${r.dataId}`,
      dataId: r.dataId,
      values: cloneValues(r.values),
      displays: cloneValues(r.displays),
    }));
    if (activeEdit && activeEdit.row.dataId == null) {
      persisted.push({
        key: activeEdit.row.key,
        dataId: null,
        values: cloneValues(activeEdit.row.values),
        // Drafts don't have server-resolved displays yet; fall back to
        // the raw values so the row still renders deterministically
        // if the user toggles out of edit before saving.
        displays: cloneValues(activeEdit.row.values),
      });
    }
    return persisted;
  }, [state, activeEdit]);

  /** Case-insensitive filter over the full list. Matches reference-type
   *  cells against their resolved display (so the user can type a hunt
   *  group name even though the stored value is an id). */
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((row) => {
      for (let i = 0; i < visibleColumns.length; i++) {
        const col = visibleColumns[i];
        const slot = col.slot;
        const display = row.displays[slot - 1] ?? row.values[slot - 1] ?? "";
        if (display && display.toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [allRows, search, visibleColumns]);

  // Back to page 1 whenever the filter or dataset changes — search runs
  // over *all* rows (filteredRows), paging only slices the result.
  useEffect(() => {
    setPage(1);
  }, [search, allRows.length]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const visibleRows = useMemo(
    () => filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredRows, safePage],
  );

  // --- edit state machine ------------------------------------------

  const isEditing = activeEdit != null;
  // Any row is considered "locked out of Edit" while another row is
  // dirty; this mirrors the "V or X first" rule the user asked for.
  const lockedByDirtyEdit = Boolean(activeEdit?.isDirty);

  const startEdit = (row: EditableRow) => {
    if (isEditing) return;
    setActiveEdit({
      row: {
        key: row.key,
        dataId: row.dataId,
        values: cloneValues(row.values),
        displays: cloneValues(row.displays),
      },
      initialValues: cloneValues(row.values),
      isDirty: false,
    });
    setEditError(null);
  };

  const updateEditCell = (slot: number, nextValue: string) => {
    setActiveEdit((prev) => {
      if (!prev) return prev;
      const values = patchValues(prev.row.values, slot, nextValue);
      const dirty = !valuesEqual(values, prev.initialValues);
      return {
        ...prev,
        row: { ...prev.row, values },
        isDirty: dirty,
      };
    });
  };

  const cancelEdit = () => {
    if (!activeEdit) return;
    setActiveEdit(null);
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!activeEdit || busy) return;
    const { row, initialValues } = activeEdit;
    const hasAnyValue = row.values.some((v) => typeof v === "string" && v.length > 0);

    // Draft with nothing typed → treat Save like Cancel; we don't
    // want to persist fully-empty rows and the user probably just
    // decided they didn't need the new entry after all.
    if (row.dataId == null && !hasAnyValue) {
      cancelEdit();
      return;
    }

    // Persisted row with unchanged values → nothing to save, just exit.
    if (row.dataId != null && valuesEqual(row.values, initialValues)) {
      cancelEdit();
      return;
    }

    setBusy(true);
    setEditError(null);
    try {
      if (row.dataId != null) {
        await dataSheetsApi.updateRecord(sheetId, row.dataId, {
          values: row.values,
        });
      } else {
        await dataSheetsApi.createRecord(sheetId, {
          values: row.values,
        });
      }
      // Re-fetch the full records list so reference-typed displays
      // resolve to names again; simpler and more correct than patching
      // the state incrementally.
      const resp = await dataSheetsApi.listRecords(sheetId);
      setState((prev) =>
        prev.status === "ready"
          ? { ...prev, records: resp.items ?? [] }
          : prev,
      );
      setActiveEdit(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save row";
      setEditError(message);
    } finally {
      setBusy(false);
    }
  };

  // --- row-level mutations -----------------------------------------

  const onAddRow = () => {
    if (isEditing) return;
    const newKey = `draft-${cryptoRandomId()}`;
    setActiveEdit({
      row: {
        key: newKey,
        dataId: null,
        values: emptyValueArray(),
        displays: emptyValueArray(),
      },
      initialValues: emptyValueArray(),
      // A brand new row counts as "something to save" immediately so
      // the Save button lights up as soon as it's created. We still
      // treat the row as not-dirty-yet for the "other rows locked"
      // check — otherwise the Add Row flow itself would lock you out.
      isDirty: false,
    });
    setEditError(null);
  };

  const requestDeleteRow = (row: EditableRow) => {
    if (isEditing) return;
    if (row.dataId == null) return;
    setPendingDeleteRow(row);
  };

  const performDeleteRow = async () => {
    const row = pendingDeleteRow;
    if (!row || row.dataId == null) return;

    setPendingDeleteRow(null);
    setBusy(true);
    setRowErrors((prev) => {
      const n = { ...prev };
      delete n[row.key];
      return n;
    });
    try {
      await dataSheetsApi.removeRecord(sheetId, row.dataId);
      setState((prev) =>
        prev.status === "ready"
          ? {
              ...prev,
              records: prev.records.filter((r) => r.dataId !== row.dataId),
            }
          : prev,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete row";
      setRowErrors((prev) => ({ ...prev, [row.key]: message }));
    } finally {
      setBusy(false);
    }
  };

  const hasColumns =
    state.status === "ready" && visibleColumns.length > 0;

  return (
    <>
      <PageHeader
        title={
          state.status === "ready"
            ? state.detail.name || "Data sheet"
            : "Data sheet"
        }
        actions={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn"
              onClick={() => navigate("/data")}
              disabled={lockedByDirtyEdit}
              title={
                lockedByDirtyEdit
                  ? "Finish the current row first"
                  : "Back to data sheets"
              }
              style={{
                background: "#ffffff",
                color: "var(--portal-primary)",
                borderColor: "var(--portal-primary)",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <ArrowLeft size={16} />
              Back
            </button>
            {state.status === "ready" && canEditSchema && (
              <button
                type="button"
                className="btn"
                onClick={() => navigate(`/data/${sheetId}`)}
                disabled={lockedByDirtyEdit}
                title={
                  lockedByDirtyEdit
                    ? "Finish the current row first"
                    : "Edit schema"
                }
                style={{
                  background: "#ffffff",
                  color: "var(--portal-primary)",
                  borderColor: "var(--portal-primary)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Settings size={16} />
                Edit schema
              </button>
            )}
            {hasColumns && (
              <>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setExportOpen(true)}
                  disabled={lockedByDirtyEdit}
                  title="Export to CSV or Excel"
                  style={{ background: "#ffffff", color: "var(--portal-primary)", borderColor: "var(--portal-primary)", display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                  <Download size={16} />
                  Export
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setImportOpen(true)}
                  disabled={isEditing}
                  title="Import from CSV or Excel"
                  style={{ background: "#ffffff", color: "var(--portal-primary)", borderColor: "var(--portal-primary)", display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                  <Upload size={16} />
                  Import
                </button>
              </>
            )}
            {hasColumns && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={onAddRow}
                disabled={isEditing}
                title={
                  isEditing
                    ? "Finish the current row first"
                    : "Add a new row"
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Plus size={16} />
                Add row
              </button>
            )}
          </div>
        }
      />
      <div className="dashboard">
        {state.status === "ready" && (
          <>
            <DataSheetExportModal
              open={exportOpen}
              onClose={() => setExportOpen(false)}
              sheetName={state.detail.name}
              columns={visibleColumns}
              records={state.records}
            />
            <DataSheetImportModal
              open={importOpen}
              onClose={() => setImportOpen(false)}
              sheetId={sheetId}
              columns={visibleColumns}
              records={state.records}
              lookups={state.lookups}
              onImported={() => {
                setImportOpen(false);
                void reload();
              }}
            />
          </>
        )}
        {state.status === "loading" && <LoadingState />}
        {state.status === "error" && <ErrorState message={state.message} />}

        {state.status === "ready" && state.detail.description && (
          <div
            className="tile"
            style={{
              color: "var(--portal-muted)",
              padding: "10px 14px",
              fontSize: "0.9rem",
            }}
          >
            {state.detail.description}
          </div>
        )}

        {state.status === "ready" && visibleColumns.length === 0 && (
          <EmptyColumnsState
            canEditSchema={canEditSchema}
            onOpenSchema={() => navigate(`/data/${sheetId}`)}
          />
        )}

        {state.status === "ready" && visibleColumns.length > 0 && (
          <>
            <OverviewToolbar
              search={search}
              onSearch={setSearch}
              placeholder="Search rows"
              count={{
                matched: filteredRows.length,
                total: allRows.length,
              }}
              trailing={
                <SegmentedToggle
                  ariaLabel="Layout"
                  value={layout}
                  onChange={(next) => {
                    setLayout(next);
                    saveStoredChoice(RECORDS_LAYOUT_KEY, next);
                  }}
                  options={[
                    { value: "grid", label: "Grid", ariaLabel: "Show as a grid", icon: <Table2 size={16} /> },
                    { value: "cards", label: "Cards", ariaLabel: "Show as cards", icon: <LayoutGrid size={16} /> },
                    { value: "auto", label: "Auto", ariaLabel: "Choose automatically based on width", icon: <Wand2 size={16} /> },
                  ]}
                />
              }
            />

            <RecordsTable
              layout={layout}
              columns={visibleColumns}
              rows={visibleRows}
              lookups={state.lookups}
              activeEdit={activeEdit}
              busy={busy}
              editError={editError}
              rowErrors={rowErrors}
              onStartEdit={startEdit}
              onCancelEdit={cancelEdit}
              onSaveEdit={saveEdit}
              onCellChange={updateEditCell}
              onDeleteRow={requestDeleteRow}
            />

            {filteredRows.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  color: "var(--portal-muted)",
                  fontSize: "0.85rem",
                }}
              >
                <span aria-live="polite">
                  Showing {(safePage - 1) * PAGE_SIZE + 1}–{(safePage - 1) * PAGE_SIZE + visibleRows.length} of {filteredRows.length} rows
                </span>
                {pageCount > 1 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setPage(Math.max(1, safePage - 1))}
                      disabled={safePage <= 1 || isEditing}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem" }}
                    >
                      <ChevronLeft size={14} /> Prev
                    </button>
                    <span>Page {safePage} of {pageCount}</span>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setPage(Math.min(pageCount, safePage + 1))}
                      disabled={safePage >= pageCount || isEditing}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem" }}
                    >
                      Next <ChevronRight size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        open={pendingDeleteRow != null}
        onClose={() => {
          if (!busy) setPendingDeleteRow(null);
        }}
        title="Delete row?"
        confirmLabel="Delete"
        tone="danger"
        busy={busy}
        onConfirm={() => void performDeleteRow()}
      >
        Delete this row? This cannot be undone.
      </ConfirmModal>
    </>
  );
}

// --- table ------------------------------------------------------------

interface EditableRow {
  key: string;
  /** Null while the row is still a client-side draft. */
  dataId: number | null;
  values: (string | null)[];
  displays: (string | null)[];
}

interface ActiveEdit {
  row: EditableRow;
  initialValues: (string | null)[];
  isDirty: boolean;
}

function RecordsTable({
  layout,
  columns,
  rows,
  lookups,
  activeEdit,
  busy,
  editError,
  rowErrors,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onCellChange,
  onDeleteRow,
}: {
  layout: RecordsLayout;
  columns: DataSheetColumn[];
  rows: EditableRow[];
  lookups: LookupsResponse;
  activeEdit: ActiveEdit | null;
  busy: boolean;
  editError: string | null;
  rowErrors: Record<string, string>;
  onStartEdit: (row: EditableRow) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onCellChange: (slot: number, nextValue: string) => void;
  onDeleteRow: (row: EditableRow) => void;
}) {
  const actionColWidth = 96;
  const isEditing = activeEdit != null;

  // Dynamic layout switch. We render an invisible probe table with the
  // same content as the visible table; its natural `max-content` width
  // tells us what the table would need if laid out desktop-style. If
  // that's wider than the container, we fall back to the stacked-card
  // layout so the user never has to horizontally scroll.
  //
  // Hysteresis: we only switch back to `table` once the probe fits
  // with a small margin — prevents bouncing when a resize lands right
  // on the boundary.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const probeRef = useRef<HTMLTableElement | null>(null);
  /** Horizontal-scroll affordance: which edges still hide columns. The
   *  sticky actions column otherwise masks that the grid continues. */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hint, setHint] = useState({ left: false, right: false });
  const updateHint = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const next = { left: el.scrollLeft > 2, right: max - el.scrollLeft > 2 };
    setHint((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
  };
  useEffect(() => {
    updateHint();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateHint);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, rows, isEditing]);
  // Measured ("auto") mode: what the probe says would fit. The user's
  // explicit Grid / Cards choice overrides it below.
  const [measured, setMeasured] = useState<"table" | "cards">("table");
  const mode: "table" | "cards" =
    layout === "auto" ? measured : layout === "grid" ? "table" : "cards";
  // Rows are always edited in the card-style modal (never inline), in
  // both grid and cards mode; the table only ever renders display cells.
  const useModalEdit = true;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const recompute = () => {
      const probe = probeRef.current;
      const containerWidth = container.clientWidth;
      if (!probe || containerWidth <= 0) return;
      const need = probe.scrollWidth;
      // Use a slightly higher threshold to switch back so we don't
      // oscillate on minor width changes (scroll bar appearing etc).
      if (need > containerWidth) {
        setMeasured("cards");
      } else if (need + 24 <= containerWidth) {
        setMeasured("table");
      }
    };

    recompute();

    const resizeObserver = new ResizeObserver(recompute);
    resizeObserver.observe(container);
    if (probeRef.current) resizeObserver.observe(probeRef.current);
    window.addEventListener("resize", recompute);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", recompute);
    };
    // Re-measure whenever the rendered content changes shape, since
    // adding columns or loading more rows can change the natural width.
  }, [columns, rows.length, isEditing]);

  const tableClass =
    mode === "cards"
      ? "ds-records-table ds-records-table--cards"
      : "ds-records-table";

  return (
    <div
      className="tile ds-records-tile"
      style={{
        padding: 0,
      }}
      ref={containerRef}
    >
      <div className="ds-records-scroll" ref={scrollRef} onScroll={updateHint}>
      <table className={tableClass}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.slot}
                title={c.name ?? ""}
              >
                {c.name || `Column ${c.slot}`}
              </th>
            ))}
            <th
              className="ds-records-actions-head"
              style={{ width: actionColWidth, textAlign: "right" }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr className="ds-records-empty-row">
              <td colSpan={columns.length + 1}>
                No rows yet. Use "Add row" below to create one.
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const editing = activeEdit?.row.key === row.key;
            // When editing, draw from the active-edit snapshot so
            // uncommitted cell values show up immediately; in
            // display mode we draw from the read-only row.
            const effectiveRow = editing ? activeEdit!.row : row;

            return (
              <tr
                key={row.key}
                className={`ds-records-row${editing ? " ds-records-row--editing" : ""}`}
              >
                {columns.map((col) => (
                  <td
                    key={col.slot}
                    data-label={col.name || `Column ${col.slot}`}
                  >
                    <DisplayCell
                      column={col}
                      value={effectiveRow.values[col.slot - 1] ?? ""}
                      display={effectiveRow.displays[col.slot - 1] ?? ""}
                    />
                  </td>
                ))}
                <td
                  className="ds-records-actions-cell"
                  style={{ width: actionColWidth }}
                >
                    <DisplayActionGroup
                      onEdit={() => onStartEdit(row)}
                      onDelete={() => onDeleteRow(row)}
                      disabled={isEditing || busy}
                      canDelete={row.dataId != null}
                    />
                  </td>
                </tr>
              );
            })}
          {Object.keys(rowErrors).length > 0 && (
            <tr className="ds-records-error-row">
              <td colSpan={columns.length + 1}>
                {Object.values(rowErrors)[0]}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      {mode === "table" && hint.right && (
        <div className="ds-records-fade ds-records-fade--right" style={{ right: actionColWidth }} aria-hidden="true" />
      )}
      {mode === "table" && hint.left && (
        <div className="ds-records-fade ds-records-fade--left" aria-hidden="true" />
      )}

      {activeEdit && useModalEdit && (
        <RecordEditModal
          columns={columns}
          row={activeEdit.row}
          lookups={lookups}
          busy={busy}
          error={editError}
          onChange={onCellChange}
          onSave={onSaveEdit}
          onCancel={onCancelEdit}
        />
      )}

      {/* Offscreen probe table: same columns + display content as the
          visible table, laid out as a classic table no matter which
          mode the visible one is in. We read its scrollWidth to decide
          whether the visible table can fit in the current container. */}
      <ProbeTable
        ref={probeRef}
        columns={columns}
        rows={rows}
        actionColWidth={actionColWidth}
        editing={false}
      />

    </div>
  );
}

const ProbeTable = forwardRef<
  HTMLTableElement,
  {
    columns: DataSheetColumn[];
    rows: EditableRow[];
    actionColWidth: number;
    /** When a row is being edited, cells must be at least as wide as the
     *  edit controls; otherwise the probe under-measures and the visible
     *  table overflows (clipping the save/cancel column). */
    editing: boolean;
  }
>(function ProbeTable({ columns, rows, actionColWidth, editing }, ref) {
  return (
    <table
      ref={ref}
      className="ds-records-probe"
      aria-hidden="true"
      tabIndex={-1}
    >
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.slot}>{c.name || `Column ${c.slot}`}</th>
          ))}
          <th style={{ width: actionColWidth }} />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            {columns.map((col) => {
              const display = row.displays[col.slot - 1] ?? row.values[col.slot - 1] ?? "";
              return (
                <td key={col.slot}>
                  {editing ? (
                    <div style={{ minWidth: EDIT_CELL_MIN_WIDTH }}>{display || "—"}</div>
                  ) : (
                    display || "—"
                  )}
                </td>
              );
            })}
            <td style={{ width: actionColWidth }}>&nbsp;</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
});

/**
 * Card-style editor for one row, used when the grid is too wide to edit
 * inline. Same cells, callbacks and error surface as the inline editor —
 * only the layout differs.
 */
function RecordEditModal({
  columns,
  row,
  lookups,
  busy,
  error,
  onChange,
  onSave,
  onCancel,
}: {
  columns: DataSheetColumn[];
  row: EditableRow;
  lookups: LookupsResponse;
  busy: boolean;
  error: string | null;
  onChange: (slot: number, nextValue: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open
      onClose={() => {
        if (!busy) onCancel();
      }}
      title={row.dataId == null ? "New row" : "Edit row"}
      busy={busy}
      maxWidth={720}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={onSave} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
        {columns.map((col) => (
          <label key={col.slot} style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>{col.name || `Column ${col.slot}`}</span>
            <EditableCell
              column={col}
              value={row.values[col.slot - 1] ?? ""}
              lookups={lookups}
              disabled={busy}
              onChange={(next) => onChange(col.slot, next)}
            />
          </label>
        ))}
      </div>
      {error && (
        <div style={{ marginTop: 12, color: "#c0392b", background: "#fdecea", border: "1px solid #e74c3c", borderRadius: 8, padding: 10, fontSize: "0.9rem" }}>
          {error}
        </div>
      )}
    </Modal>
  );
}

function DisplayActionGroup({
  onEdit,
  onDelete,
  disabled,
  canDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
  disabled: boolean;
  canDelete: boolean;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 6,
      }}
    >
      <button
        type="button"
        onClick={onEdit}
        disabled={disabled}
        title={disabled ? "Finish the current row first" : "Edit row"}
        aria-label="Edit row"
        className="btn"
        style={iconBtnStyle(disabled)}
      >
        <Pencil size={14} />
      </button>
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled}
          title={disabled ? "Finish the current row first" : "Delete row"}
          aria-label="Delete row"
          className="btn"
          style={iconBtnStyle(disabled)}
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

function iconBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "4px 8px",
    background: "#ffffff",
    color: disabled ? "var(--portal-muted)" : undefined,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}

// --- cells ------------------------------------------------------------

function DisplayCell({
  column,
  value,
  display,
}: {
  column: DataSheetColumn;
  value: string;
  display: string;
}) {
  const type = column.typeId;
  const emptyStyle: React.CSSProperties = {
    color: "var(--portal-muted)",
    fontStyle: "italic",
  };

  if (type === DATA_SHEET_COL_TYPE.checkbox) {
    const checked = value === "1" || value.toLowerCase() === "true";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontWeight: 600,
          color: checked
            ? "var(--portal-status-success-darker, #2f855a)"
            : "var(--portal-muted)",
        }}
      >
        {checked ? (
          <>
            <Check size={14} /> Yes
          </>
        ) : (
          "No"
        )}
      </span>
    );
  }

  if (!value) {
    return <span style={emptyStyle}>—</span>;
  }

  if (
    type === DATA_SHEET_COL_TYPE.huntGroup ||
    type === DATA_SHEET_COL_TYPE.agent ||
    type === DATA_SHEET_COL_TYPE.voice ||
    type === DATA_SHEET_COL_TYPE.weekplan
  ) {
    // Use the resolved display name when available; fall back to an
    // "id + (not found)" hint when the reference target was deleted.
    if (display && display !== value) {
      return <span>{display}</span>;
    }
    return (
      <span style={emptyStyle}>
        #{value} (not found)
      </span>
    );
  }

  if (type === DATA_SHEET_COL_TYPE.date) {
    return <span>{formatIsoDateForDisplay(value)}</span>;
  }
  if (type === DATA_SHEET_COL_TYPE.time) {
    return <span>{formatIsoTimeForDisplay(value)}</span>;
  }

  return <span>{value}</span>;
}

function EditableCell({
  column,
  value,
  lookups,
  disabled,
  onChange,
}: {
  column: DataSheetColumn;
  value: string;
  lookups: LookupsResponse;
  disabled: boolean;
  onChange: (next: string) => void;
}) {
  const type = column.typeId;

  const lookupOptions = useMemo<LookupItem[]>(() => {
    switch (type) {
      case DATA_SHEET_COL_TYPE.huntGroup: return lookups.huntGroups;
      case DATA_SHEET_COL_TYPE.agent: return lookups.agents;
      case DATA_SHEET_COL_TYPE.voice: return lookups.voices;
      case DATA_SHEET_COL_TYPE.weekplan: return lookups.weekplans;
      default: return [];
    }
  }, [type, lookups]);

  if (
    type === DATA_SHEET_COL_TYPE.huntGroup ||
    type === DATA_SHEET_COL_TYPE.agent ||
    type === DATA_SHEET_COL_TYPE.voice ||
    type === DATA_SHEET_COL_TYPE.weekplan
  ) {
    const known = lookupOptions.some((o) => String(o.id) === value);
    return (
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="portal-input-inline"
        style={cellInputStyle()}
      >
        <option value="">—</option>
        {!known && value && (
          <option value={value}>#{value} (not found)</option>
        )}
        {lookupOptions.map((o) => (
          <option key={o.id} value={String(o.id)}>
            {o.name}
          </option>
        ))}
      </select>
    );
  }

  if (type === DATA_SHEET_COL_TYPE.checkbox) {
    const checked = value === "1" || value.toLowerCase() === "true";
    return (
      <div style={{ padding: "2px 2px" }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked ? "1" : "0")}
          style={{
            width: 18,
            height: 18,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
          aria-label={column.name}
        />
      </div>
    );
  }

  if (type === DATA_SHEET_COL_TYPE.date) {
    return (
      <input
        type="date"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="portal-input-inline"
        style={cellInputStyle()}
      />
    );
  }

  if (type === DATA_SHEET_COL_TYPE.time) {
    return (
      <input
        type="time"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="portal-input-inline"
        style={cellInputStyle()}
      />
    );
  }

  if (type === DATA_SHEET_COL_TYPE.number) {
    return (
      <input
        type="number"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="portal-input-inline"
        style={cellInputStyle()}
      />
    );
  }

  return (
    <input
      type="text"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="portal-input-inline"
      style={cellInputStyle()}
    />
  );
}

function cellInputStyle(): React.CSSProperties {
  return {
    width: "100%",
    minWidth: EDIT_CELL_MIN_WIDTH,
    padding: "6px 8px",
    border: "1px solid var(--portal-border)",
    borderRadius: 6,
    background: "#ffffff",
    fontFamily: "inherit",
    fontSize: "0.9rem",
  };
}

// --- helpers ----------------------------------------------------------

function emptyValueArray(): (string | null)[] {
  return Array.from({ length: 10 }, () => null);
}

function cloneValues(
  values: (string | null)[] | null | undefined,
): (string | null)[] {
  const out = emptyValueArray();
  if (!values) return out;
  for (let i = 0; i < values.length && i < 10; i++) out[i] = values[i] ?? null;
  return out;
}

function patchValues(
  values: (string | null)[],
  slot: number,
  nextValue: string,
): (string | null)[] {
  const next = cloneValues(values);
  next[slot - 1] = nextValue === "" ? null : nextValue;
  return next;
}

function valuesEqual(a: (string | null)[], b: (string | null)[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const av = a[i] ?? "";
    const bv = b[i] ?? "";
    if (av !== bv) return false;
  }
  return true;
}

function formatIsoDateForDisplay(iso: string): string {
  // YYYY-MM-DD → locale-ish dd MMM yyyy to match the rest of the portal.
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  const monthIdx = Number(m) - 1;
  if (monthIdx < 0 || monthIdx > 11) return iso;
  const monthLabels = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${Number(d)} ${monthLabels[monthIdx]} ${y}`;
}

function formatIsoTimeForDisplay(t: string): string {
  // Browsers sometimes surface "HH:mm:ss" from <input type="time">.
  // We only need HH:mm for display consistency with the old portal.
  const bits = t.split(":");
  if (bits.length < 2) return t;
  return `${bits[0]}:${bits[1]}`;
}

function cryptoRandomId(): string {
  // Small standalone helper so the module still works under jsdom /
  // very old Safaris that don't expose crypto.randomUUID.
  if (
    typeof crypto !== "undefined" &&
    typeof (crypto as Crypto).randomUUID === "function"
  ) {
    return (crypto as Crypto).randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function LoadingState() {
  return (
    <div className="tile" style={{ color: "var(--portal-muted)" }}>
      Loading data sheet…
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div
      className="tile"
      style={{
        borderColor: "#e74c3c",
        color: "#c0392b",
        background: "#fdecea",
      }}
    >
      <strong>Something went wrong.</strong>
      <div style={{ marginTop: 6, fontSize: "0.9rem" }}>{message}</div>
    </div>
  );
}

function EmptyColumnsState({
  canEditSchema,
  onOpenSchema,
}: {
  canEditSchema: boolean;
  onOpenSchema: () => void;
}) {
  return (
    <section className="tile" style={{ maxWidth: 720 }}>
      <h3 style={{ marginTop: 0 }}>No columns yet</h3>
      <p>
        This data sheet doesn't have any columns defined, so there's
        nothing to capture per row.{" "}
        {canEditSchema
          ? "Open the schema editor to add your first column."
          : "Ask an administrator to define columns for this sheet."}
      </p>
      {canEditSchema && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={onOpenSchema}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Settings size={16} />
          Open schema editor
        </button>
      )}
    </section>
  );
}
